/**
 * A conversa — de uma frente de trabalho, de um card do Dev Board ou de uma seção do GDD.
 *
 * <p>Na frente e no card, vive numa aba do modal, ao lado do checklist. As duas coisas
 * competiam pelo mesmo corpo rolável — checklist é lista de afazeres, conversa é
 * histórico —, e empilhar as duas fazia a frente com vinte comentários esconder o
 * checklist embaixo de tudo. No GDD, ela fica embaixo de cada seção, que é onde a
 * revisão acontece.
 *
 * <p><b>Chamava-se {@code ComentariosDaFrente}</b>, e o nome deixou de ser verdade
 * quando a revisão do GDD passou a usar a mesma conversa. Duplicá-la teria sido pior: a
 * menção que notifica, o anexo e o editar/apagar valem nos dois lugares, e duas cópias
 * divergiriam no primeiro recurso que só uma delas ganhasse.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api } from '../api/cliente';
import { abrirAnexo } from '../comum/abrirAnexo';
import { ImagemAutenticada } from '../comum/ImagemAutenticada';
import { tamanhoLegivel as tamanho } from '../comum/tamanhoLegivel';
import type { Membro } from '../api/tipos';
import { CaixaComMencao } from './CaixaComMencao';
import { TextoRico } from './TextoRico';
import {
  chaveComentarios,
  rotaDaConversa,
  subirAnexo,
  useMutacoesComentario,
} from './comentariosApi';
import type { AnexoDeComentario, Comentario, DonoDaConversa } from './comentariosApi';
import { Avatar } from '../comum/Avatar';
import { tempoRelativo } from '../comum/tempoRelativo';
import './comentarios.css';


const dataCheia = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });


const ehImagem = (a: AnexoDeComentario) => Boolean(a.miniatura);

export function Conversa({
  dono,
  membros,
}: {
  /** De quem é: a frente do roadmap, o card do Dev Board ou a seção do GDD. */
  dono: DonoDaConversa;
  membros: Membro[];
}) {
  const { criar, editar, excluir } = useMutacoesComentario(dono);

  const [texto, setTexto] = useState('');
  const [pendentes, setPendentes] = useState<AnexoDeComentario[]>([]);
  const [subindo, setSubindo] = useState(0);
  const [erroDoAnexo, setErroDoAnexo] = useState<string | null>(null);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [rascunho, setRascunho] = useState('');
  const campoDeArquivo = useRef<HTMLInputElement>(null);
  const fim = useRef<HTMLDivElement>(null);

  const { data: comentarios, isPending } = useQuery<Comentario[]>({
    queryKey: chaveComentarios(dono),
    queryFn: () => api(rotaDaConversa(dono)),
  });

  // Abrir a aba mostra o fim da conversa, que é onde está o que se quer ler.
  useEffect(() => {
    fim.current?.scrollIntoView({ block: 'nearest' });
  }, [comentarios?.length]);

  /**
   * Sobe os anexos escolhidos, um a um.
   *
   * <p><b>O erro é mostrado.</b> Este bloco tinha um {@code catch} vazio: quando o upload
   * falhava — formato recusado, arquivo grande demais —, o contador zerava, nada aparecia
   * na lista e ninguém dizia por quê. O sintoma era exatamente "está enviando e sumindo".
   *
   * <p>Um arquivo que falha não impede os outros: numa seleção de cinco, o quarto ser
   * grande demais não é motivo para descartar os três que já subiram.
   */
  async function anexar(arquivos: FileList | null) {
    if (!arquivos?.length) return;
    setErroDoAnexo(null);
    const falhas: string[] = [];

    for (const arquivo of Array.from(arquivos)) {
      setSubindo((n) => n + 1);
      try {
        const salvo = await subirAnexo(arquivo);
        setPendentes((atuais) => [...atuais, salvo]);
      } catch (e) {
        falhas.push(`${arquivo.name}: ${e instanceof Error ? e.message : 'falhou'}`);
      } finally {
        setSubindo((n) => n - 1);
      }
    }

    if (falhas.length) setErroDoAnexo(falhas.join(' · '));
    if (campoDeArquivo.current) campoDeArquivo.current.value = '';
  }

  function enviar() {
    const limpo = texto.trim();
    // Anexo sem texto é comentário válido — arrastar um print e mandar é uma mensagem.
    if (!limpo && pendentes.length === 0) return;
    criar.mutate(
      { texto: limpo || ' ', anexos: pendentes },
      {
        onSuccess: () => {
          setTexto('');
          setPendentes([]);
        },
      }
    );
  }

  const lista = comentarios ?? [];

  return (
    <div className="cm">
      <div className="cm-lista">
        {isPending && <div className="cm-vazio">carregando a conversa…</div>}

        {!isPending && lista.length === 0 && (
          <div className="cm-vazio">
            <b>Nenhum comentário ainda</b>
            Registre aqui o que foi decidido — e para quem.
            <br />
            Use <span className="cm-mencao">@handle</span> para citar alguém do time.
          </div>
        )}

        {lista.map((c) => (
          <div key={c.id} className="cm-item">
            <Avatar
              className="cm-av"
              nome={c.autorNome}
              cor={c.autorCor}
              url={c.autorAvatar}
              title={c.autorNome ?? 'usuário removido'}
            />

            <div className="cm-corpo">
              <div className="cm-topo">
                <b className={c.autorNome ? '' : 'cm-sem-autor'}>
                  {c.autorNome ?? 'usuário removido'}
                </b>
                <span className="cm-quando" title={dataCheia(c.criadoEm)}>
                  {tempoRelativo(c.criadoEm)}
                </span>
                {c.editadoEm && (
                  <span className="cm-editado" title={`Editado em ${dataCheia(c.editadoEm)}`}>
                    editado
                  </span>
                )}
                {c.meu && editandoId !== c.id && (
                  <span className="cm-acoes">
                    <button
                      onClick={() => {
                        setEditandoId(c.id);
                        setRascunho(c.texto);
                      }}
                    >
                      editar
                    </button>
                    <button
                      className="perigo"
                      onClick={() => {
                        if (confirm('Apagar este comentário?')) excluir.mutate(c.id);
                      }}
                    >
                      apagar
                    </button>
                  </span>
                )}
              </div>

              {editandoId === c.id ? (
                <div className="cm-editor">
                  <CaixaComMencao
                    valor={rascunho}
                    aoMudar={setRascunho}
                    membros={membros}
                    autoFocus
                    rows={3}
                    aoTeclar={(e) => {
                      if (e.key === 'Escape') setEditandoId(null);
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        editar.mutate(
                          { id: c.id, texto: rascunho.trim() },
                          { onSuccess: () => setEditandoId(null) }
                        );
                      }
                    }}
                  />
                  <div className="cm-editor-acoes">
                    <button
                      className="cm-enviar"
                      disabled={!rascunho.trim()}
                      onClick={() =>
                        editar.mutate(
                          { id: c.id, texto: rascunho.trim() },
                          { onSuccess: () => setEditandoId(null) }
                        )
                      }
                    >
                      Salvar
                    </button>
                    <button className="cm-cancelar" onClick={() => setEditandoId(null)}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="cm-texto">
                  <TextoRico texto={c.texto} membros={membros} />
                </div>
              )}

              {c.anexos.length > 0 && (
                <div className="cm-anexos">
                  {c.anexos.map((a) => (
                    <button
                      key={a.id ?? a.url}
                      type="button"
                      className={`cm-anexo${ehImagem(a) ? ' img' : ''}`}
                      // Botão com fetch autenticado, e não <a target=_blank>: a aba nova
                      // não manda o token, e o servidor devolvia 401 no lugar da imagem.
                      // O nome original continua valendo no download, como o `?nome=`
                      // do link antigo prometia.
                      onClick={() => void abrirAnexo(a.url, a.nomeOriginal)}
                      title={`${a.nomeOriginal} · ${tamanho(a.bytes)}`}
                    >
                      {ehImagem(a) ? (
                        <ImagemAutenticada url={a.miniatura ?? a.url} alt={a.nomeOriginal} />
                      ) : (
                        <>
                          <span className="cm-ext">
                            {(a.nomeOriginal.split('.').pop() ?? '?').slice(0, 4).toUpperCase()}
                          </span>
                          <span className="cm-nome">{a.nomeOriginal}</span>
                          <span className="cm-bytes">{tamanho(a.bytes)}</span>
                        </>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={fim} />
      </div>

      {/* ------------------------------------------------------- escrever */}

      <div className="cm-escrever">
        {(pendentes.length > 0 || subindo > 0) && (
          <div className="cm-pendentes">
            {pendentes.map((a, i) => (
              <span key={a.url} className="cm-pendente" title={`${a.nomeOriginal} · ${tamanho(a.bytes)}`}>
                {ehImagem(a) ? (
                  <ImagemAutenticada url={a.miniatura ?? a.url} alt="" />
                ) : (
                  <span className="cm-ext">📎</span>
                )}
                <span className="cm-nome">{a.nomeOriginal}</span>
                <button
                  aria-label={`Tirar ${a.nomeOriginal}`}
                  onClick={() => setPendentes((atuais) => atuais.filter((_, j) => j !== i))}
                >
                  ×
                </button>
              </span>
            ))}
            {subindo > 0 && <span className="cm-subindo">enviando {subindo}…</span>}
          </div>
        )}

        {erroDoAnexo && (
          <div className="cm-erro-anexo" role="alert">
            {erroDoAnexo}
            <button onClick={() => setErroDoAnexo(null)} aria-label="Fechar">
              ×
            </button>
          </div>
        )}

        {/* O envio que falha diz por quê. Sem isto, o botão voltava de "enviando…" para
            "Comentar" com o texto ainda na caixa e nenhum aviso — parecia que não tinha
            acontecido nada, quando o servidor tinha recusado. */}
        {criar.isError && (
          <div className="cm-erro-anexo" role="alert">
            Não foi possível enviar o comentário: {criar.error.message}
            <button onClick={() => criar.reset()} aria-label="Fechar">
              ×
            </button>
          </div>
        )}

        <div className="cm-linha">
          <CaixaComMencao
            valor={texto}
            aoMudar={setTexto}
            membros={membros}
            rows={2}
            placeholder="Escreva um comentário… digite @ para citar alguém"
            // Enter quebra linha; Ctrl/Cmd+Enter envia. Numa caixa que aceita texto
            // longo, Enter enviando é o atalho que manda mensagem pela metade.
            aoTeclar={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) enviar();
            }}
          />
          <div className="cm-botoes">
            <button
              className="cm-anexar"
              title="Anexar arquivo"
              onClick={() => campoDeArquivo.current?.click()}
            >
              📎
            </button>
            <button
              className="cm-enviar"
              disabled={criar.isPending || (!texto.trim() && pendentes.length === 0)}
              onClick={enviar}
            >
              {criar.isPending ? 'enviando…' : 'Comentar'}
            </button>
          </div>
        </div>
        <div className="cm-dica">
          Ctrl+Enter envia · @ cita alguém · anexe imagens, PDF ou o que precisar
        </div>

        <input
          ref={campoDeArquivo}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => void anexar(e.target.files)}
        />
      </div>
    </div>
  );
}
